import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  selectCurrentUserRoles,
  selectImpersonatedUserName,
  selectIsImpersonating,
  selectProfile,
} from '../state/current-user/current-user.selectors';
import { map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  DsButtonModule,
  DsLoadingAreaModule,
  DsLoadingAreaOverlayContainerDirective,
  DsLoadingAreaService,
  DsMessageModule,
  DsModalModule,
} from '@bmw-ds/components';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';
import { SwitchLanguageComponent } from '../shared/switch-language/switch-language.component';
import { SwitchCountryComponent } from '../shared/switch-country/switch-country.component';
import { BreadcrumbsComponent } from '../shared/breadcrumbs/breadcrumbs.component';
import { NgClass } from '@angular/common';
import { Roles } from '../shared/enums/roles.enum';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ColDef } from 'ag-grid-community';
import { IHelpDocumentsFromProfile } from '../admin/countries/countries.models';
import { BooleanCellRendererComponent } from '../shared/boolean-cell-renderer/boolean-cell-renderer.component';
import { HelpDocumentDownloadCellRendererComponent } from './cell-renderers/download-cell-renderer/action-cell-renderer.component';
import { DsModalWrapperComponent } from '../common/ds-modal-wrapper/ds-modal-wrapper.component';
import { Vin17SearchComponent } from '../shared/vin17-search/vin17-search.component';
import { CommonTableComponent } from '../shared/common-table/common-table.component';
import { injectActiveLanguageSignal } from '../shared/utils/active-language';
import { loadHelpDocuments } from './state/help-documents.actions';
import {
  selectHelpDocuments,
  selectHelpDocumentsLoading,
} from './state/help-documents.selectors';

const HELP_DOCUMENTS_LOADING_AREA = 'help-documents-loading-area';

@Component({
  selector: 'tqc-header',
  imports: [
    DsButtonModule,
    SwitchCountryComponent,
    SwitchLanguageComponent,
    BreadcrumbsComponent,
    NgClass,
    RouterLink,
    TranslatePipe,
    DsModalModule,
    DsLoadingAreaModule,
    DsMessageModule,
    DsModalWrapperComponent,
    Vin17SearchComponent,
    CommonTableComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
  private readonly store = inject(Store);
  public readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly dsLoadingAreaService = inject(DsLoadingAreaService);
  private readonly activeLanguage = injectActiveLanguageSignal();

  private readonly helpDocumentsContainer = viewChild(
    DsLoadingAreaOverlayContainerDirective
  );

  public readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects.split('?')[0])
    ),
    { initialValue: this.router.url }
  );

  public readonly isHelpDocumentsModalOpen = signal(false);

  public readonly columnDefinition = computed<
    ColDef<IHelpDocumentsFromProfile & { actions?: string }>[]
  >(() => {
    this.activeLanguage();
    return [
      {
        field: 'subject',
        headerName: this.translate.instant(
          'header.helpDocuments.columns.subject'
        ),
      },
      {
        field: 'active',
        headerName: this.translate.instant(
          'header.helpDocuments.columns.active'
        ),
        cellRenderer: BooleanCellRendererComponent,
      },
      {
        field: 'countryCodes',
        headerName: this.translate.instant(
          'header.helpDocuments.columns.countries'
        ),
        valueGetter: (params) => params.data?.countryCodes?.join(', '),
      },
      {
        field: 'actions',
        cellRenderer: HelpDocumentDownloadCellRendererComponent,
      },
    ];
  });

  public readonly helpDocuments = this.store.selectSignal(selectHelpDocuments);
  private readonly helpDocumentsLoading = this.store.selectSignal(
    selectHelpDocumentsLoading
  );

  public readonly userFirstName = toSignal(
    this.store.select(selectProfile).pipe(map((profile) => profile?.firstName))
  );

  private readonly userRoles = toSignal(
    this.store.select(selectCurrentUserRoles),
    { initialValue: [] }
  );

  public readonly hasDamageEntryAccess = computed(() =>
    this.userRoles().some(
      (role) =>
        role.isActive &&
        (role.name === Roles.SUPER_USER || role.name === Roles.DAMAGE_ENTRY)
    )
  );

  public readonly isImpersonating = toSignal(
    this.store.select(selectIsImpersonating),
    { initialValue: false }
  );
  public readonly impersonatedUserName = toSignal(
    this.store.select(selectImpersonatedUserName),
    { initialValue: null }
  );
  public readonly impersonationMessage = computed(() => {
    const name = this.impersonatedUserName();
    return name
      ? `${this.translate.instant('header.impersonating')} ${name}`
      : '';
  });

  public readonly isDashboardUrl = computed(
    () => this.currentUrl() === '/dashboard'
  );

  constructor() {
    effect(() => {
      this.syncLoadingArea(
        this.helpDocumentsLoading(),
        this.helpDocumentsContainer()
      );
    });
  }

  public navigateToVin(vin17: string | null): void {
    if (vin17 !== null) {
      void this.router.navigate(['/vin', vin17]);
    }
  }

  public openModal(): void {
    this.isHelpDocumentsModalOpen.set(true);
    this.store.dispatch(loadHelpDocuments());
  }

  private syncLoadingArea(
    loading: boolean,
    container: DsLoadingAreaOverlayContainerDirective | undefined
  ): void {
    if (!container) {
      return;
    }
    if (loading) {
      this.dsLoadingAreaService.addLoadingArea({
        id: HELP_DOCUMENTS_LOADING_AREA,
        container,
        loadingText: 'Loading help documents',
      });
    } else {
      this.dsLoadingAreaService.removeLoadingArea(HELP_DOCUMENTS_LOADING_AREA);
    }
  }
}
